import Icon from '@atoms/Icon/Icon';
import Image from '@atoms/Image/Image';
import Video from '@atoms/Video/Video';
import { AspectRatioType } from '@models/IAspectRatio';
import { IImage } from '@models/IImage';
import { IMeta } from '@models/IMeta';
import { IVideo } from '@models/IVideo';
import { IVideoSubtitle } from '@models/IVideoSubtitle';
import useAriaLabels from '@utils/hooks/useAriaLabels';
import classNames from 'classnames';
import { AnimatePresence, MotionValue, motion, useInView } from 'framer-motion';
import { RefObject, useEffect, useRef, useState } from 'react';

interface Props {
  readonly autoPlay?: boolean;
  readonly controls?: boolean;
  readonly coverImage?: IImage;
  readonly dataTestId?: string;
  readonly hasInVideoPlayButton?: boolean;
  readonly inVideoPlayButtonClassName?: string;
  readonly inVideoPlayButtonPositionClassName?: string;
  readonly inVideoPlayButtonTabIndex?: number;
  readonly inViewThreshold?: number;
  readonly meta?: IMeta;
  readonly playOnCoverClick?: boolean;
  readonly subtitles?: IVideoSubtitle[];
  readonly video: IVideo;
  readonly videoClasses?: string;
  readonly videoRatio?: AspectRatioType;
  readonly videoWrapperOpacityProgress?: MotionValue<number>;
  readonly wrapperClasses?: string;
  readonly onFocusCallback?: (e: React.FocusEvent<HTMLButtonElement>) => void;
}

const InViewVideo = ({
  autoPlay = false,
  controls,
  coverImage,
  dataTestId = 'In-View-Video',
  hasInVideoPlayButton,
  inVideoPlayButtonClassName,
  inVideoPlayButtonPositionClassName = 'bottom-5 left-5',
  inVideoPlayButtonTabIndex = 0,
  inViewThreshold = 0.5,
  meta,
  onFocusCallback,
  playOnCoverClick = true,
  subtitles,
  video,
  videoClasses,
  videoRatio,
  videoWrapperOpacityProgress,
  wrapperClasses,
}: Props) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isVideoManuallyPaused, setIsVideoManuallyPaused] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  const videoRef = useRef<HTMLDivElement>(null);
  const inView = useInView(videoRef, { amount: inViewThreshold });
  const isAutoPlaying = !hasInVideoPlayButton
    ? autoPlay
    : autoPlay && !isVideoManuallyPaused;
  const { playVideoAriaLabel, pauseVideoAriaLabel } = useAriaLabels();

  useEffect(() => {
    setIsPlaying(isAutoPlaying && inView);
    setIsMuted(!controls || !inView || isAutoPlaying);
  }, [isAutoPlaying, inView, controls]);

  return (
    <div
      className={classNames('relative', wrapperClasses)}
      data-testid={dataTestId}
    >
      <Video
        classes={videoClasses}
        title={video?.url}
        videoSource={video?.url}
        ref={videoRef as unknown as RefObject<HTMLDivElement>}
        videoAttributes={{
          playsinline: true,
          muted: isMuted,
          playing: isPlaying,
          loop: true,
        }}
        onVideoReady={() => setVideoReady(true)}
        epiEdit={meta?.editHints?.video as string | undefined}
        controls={controls}
        videoRatio={videoRatio}
        subtitles={subtitles}
        isPlaying={isPlaying}
        setIsPlaying={setIsPlaying}
        videoWrapperOpacityProgress={videoWrapperOpacityProgress}
        onVolumeChange={(videoElement) => {
          setIsMuted(videoElement.volume === 0 || videoElement.muted);
        }}
      />

      {hasInVideoPlayButton && videoReady && (
        <button
          type="button"
          aria-label={isPlaying ? pauseVideoAriaLabel : playVideoAriaLabel}
          className={classNames(
            'absolute z-20 flex h-7.5 w-7.5 items-center justify-center rounded-full bg-black bg-opacity-50',
            inVideoPlayButtonPositionClassName,
            inVideoPlayButtonClassName
          )}
          onClick={() => {
            setIsPlaying(!isPlaying);
            setIsVideoManuallyPaused(isPlaying);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              setIsPlaying(!isPlaying);
              setIsVideoManuallyPaused(isPlaying);
            }
          }}
          onFocus={onFocusCallback}
          data-testid={`${dataTestId}-${isPlaying ? 'Pause' : 'Play'}-Button`}
          data-video-control="toggle"
          tabIndex={inVideoPlayButtonTabIndex}
        >
          <Icon
            name={isPlaying ? 'pause' : 'play_icon_filled'}
            colorClassName="text-white"
            size="1.4rem"
            className="pointer-events-none"
          />
        </button>
      )}

      {(coverImage || !isAutoPlaying) && (
        <AnimatePresence>
          {!isPlaying && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => playOnCoverClick && setIsPlaying(true)}
              className="group absolute inset-0 flex items-center justify-center hover:cursor-pointer"
            >
              {coverImage && (
                <Image
                  classes="relative w-full h-full"
                  image={coverImage}
                  imageClasses="w-full h-full object-cover object-center"
                  epiEdit={meta?.editHints?.video as string | undefined}
                  dataTestId={`${dataTestId}-Cover-Image`}
                />
              )}

              {!isAutoPlaying && coverImage && (
                <button
                  type="button"
                  aria-label={playVideoAriaLabel}
                  className="absolute bottom-2.5 left-2.5 flex items-center md:bottom-5 md:left-5"
                  onClick={() => setIsPlaying(true)}
                  data-testid={`${dataTestId}-Play-Button`}
                >
                  <div className="h-[2.1875rem] w-[2.1875rem] md:h-[2.875rem] md:w-[2.875rem]">
                    <Icon name="play_icon_circle" size="100%" />
                  </div>
                </button>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      )}
    </div>
  );
};

export default InViewVideo;
